import { Button } from '@/components/ui/button';
import { Edit } from 'lucide-react';
import { LoanResponse, LoanByIdResponse } from '@/types/api.types';
import {
  formatCurrency,
  formatDate,
  formatDecimalAsPercentage,
  formatNumber,
  getStatusColor,
} from '@/utils/helpers';
import { FormattedField } from '@/components/ui/FormattedField';
import { loanService } from '@/services/loan.service';
import { valuationService } from '@/services/valuation.service';
import { loanAnalyticsService } from '@/services/loan-analytics.service';
import { useState, useEffect } from 'react';

interface LoanDetailModalProps {
  loan: LoanResponse | null;
  onClose: () => void;
  onEdit: (loanId: number) => void;
}

export const LoanDetailModal = ({
  loan,
  onClose,
  onEdit,
}: LoanDetailModalProps) => {
  const [fullLoanData, setFullLoanData] = useState<LoanByIdResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (loan?.rp_system_id) {
      setLoading(true);
      const systemId = loan.rp_system_id;
      
      // Fetch structured loan data from multiple sources (like LoanDetailPage does)
      Promise.all([
        loanService.getLoanBySystemId(systemId).catch(() => null),
        valuationService.getPricingComponents(systemId)
          .then((data) => data.find((p) => p.loan_id === systemId) || null)
          .catch(() => null),
        loanAnalyticsService.getLoanRiskMetrics()
          .then((response) => response.loans.find((m) => m.loan_id === systemId) || null)
          .catch(() => null),
      ])
        .then(([loanDetailData, pricingData, riskMetricsData]) => {
          // Extract data from nested structure (like in loan-mapping.ts)
          const pricingResults = loanDetailData?.pricing_results;
          
          // Get credit spread: from pricing components (rpx_total_spread_bps) or risk metrics
          let creditSpread: number | null = null;
          if (pricingData) {
            // Convert from basis points to decimal (e.g., 500 bps = 0.05)
            creditSpread = pricingData.rpx_total_spread_bps / 10000;
          } else if (riskMetricsData) {
            // Convert from basis points to decimal
            creditSpread = riskMetricsData.rpx_total_spread_bps / 10000;
          }
          
          // Get market yield: from pricing_results or pricing components or risk metrics
          let marketYield: number | null = null;
          if (pricingResults?.market_yield !== null && pricingResults?.market_yield !== undefined) {
            marketYield = pricingResults.market_yield;
          } else if (pricingData) {
            // total_yield_pct is already a percentage (e.g., 5.5 = 5.5%), convert to decimal (0.055)
            marketYield = pricingData.total_yield_pct / 100;
          } else if (riskMetricsData) {
            // market_yield from risk metrics is already in decimal format
            marketYield = riskMetricsData.market_yield ?? null;
          }
          
          // Get risk metrics: from riskMetricsData (loan-analytics service) or loan
          // Note: LoanRiskMetrics has pd and lgd, but not ead
          const pd = riskMetricsData?.pd ?? loan.pd ?? null;
          const ead = loan.ead ?? null; // ead is not in LoanRiskMetrics, use from loan
          const lgd = riskMetricsData?.lgd ?? loan.lgd ?? null;
          
          // Create enhanced data with extracted values
          // Ensure all required fields from LoanByIdResponse are present
          const enhancedData: LoanByIdResponse = {
            ...loan,
            credit_spread: creditSpread ?? loan.credit_spread ?? null,
            market_yield: marketYield ?? loan.market_yield ?? null,
            pd: pd,
            ead: ead,
            lgd: lgd,
            data_completeness_score: (loan as LoanByIdResponse).data_completeness_score ?? 0,
          };
          
          setFullLoanData(enhancedData);
        })
        .catch((error) => {
          console.error('Error loading loan detail data:', error);
          // Fallback to the loan data from props
          setFullLoanData(loan as LoanByIdResponse);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (loan) {
      // If no rp_system_id, use the loan data from props
      setFullLoanData(loan as LoanByIdResponse);
    }
  }, [loan]);

  if (!loan) return null;

  // Use full loan data if available, otherwise fallback to loan from props
  const displayLoan = fullLoanData || (loan as LoanByIdResponse);

  // Helper function to convert credit spread (same logic as loan-mapping.ts)
  const getCreditSpread = (val: number | string | null | undefined): number | null => {
    if (val === null || val === undefined || val === '') return null;
    const numVal = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(numVal)) return null;
    // If value >= 1000, likely basis points (10000 bps = 100%), convert to decimal (1.0)
    if (numVal >= 1000) {
      return numVal / 10000;
    }
    // If value >= 1 and < 1000, likely percentage (100 = 100%), convert to decimal (1.0)
    if (numVal >= 1) {
      return numVal / 100;
    }
    // Otherwise assume it's already decimal (0.01 = 1%)
    return numVal;
  };

  // Helper function to convert market yield (same logic as loan-mapping.ts)
  const getMarketYield = (val: number | string | null | undefined): number | null => {
    if (val === null || val === undefined || val === '') return null;
    const numVal = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(numVal)) return null;
    // Same logic: if >= 1, convert from percentage to decimal
    return numVal >= 1 ? numVal / 100 : numVal;
  };

  // Helper function to convert risk metrics (pd, ead, lgd)
  const getRiskMetric = (val: number | string | null | undefined): number | null => {
    if (val === null || val === undefined || val === '') return null;
    const numVal = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(numVal) ? null : numVal;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Loan Details - {displayLoan.loan_name}
            </h3>
            {loading && (
              <div className="text-sm text-gray-500">Loading additional data...</div>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Loan Information */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Loan Information
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">System ID:</span>
                  <span className="font-medium">{displayLoan.rp_system_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Client Loan Number:</span>
                  <span className="font-medium">{displayLoan.client_loan_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sector:</span>
                  <span className="font-medium">{displayLoan.property_sector}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Property Type:</span>
                  <span className="font-medium">{displayLoan.property_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Borrower:</span>
                  <span className="font-medium">{displayLoan.sponsor_borrower}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`font-medium ${getStatusColor(displayLoan.loan_status)}`}
                  >
                    {displayLoan.loan_status}
                  </span>
                </div>
              </div>
            </div>

            {/* Financial Metrics */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Balance Information
              </h4>
              <div className="space-y-2 text-sm">
                <FormattedField
                  label="Original Balance"
                  value={formatCurrency(displayLoan.original_balance, 2, displayLoan.currency || 'USD')}
                  rawValue={displayLoan.original_balance}
                />
                <FormattedField
                  label="Current Balance"
                  value={formatCurrency(displayLoan.current_balance, 2, displayLoan.currency || 'USD')}
                  rawValue={displayLoan.current_balance}
                />
                <FormattedField
                  label="Currency"
                  value={displayLoan.currency || '—'}
                  rawValue={displayLoan.currency}
                />
                <FormattedField
                  label="Client Percentage"
                  value={formatDecimalAsPercentage(displayLoan.client_percentage, 1)}
                  rawValue={displayLoan.client_percentage}
                />
              </div>
            </div>

            {/* Risk Metrics */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Default Assumptions
              </h4>
              <div className="space-y-2 text-sm">
                <FormattedField
                  label="Credit Spread"
                  value={formatDecimalAsPercentage(getCreditSpread(displayLoan.credit_spread), 3)}
                  rawValue={displayLoan.credit_spread}
                />
                <FormattedField
                  label="Market Yield"
                  value={formatDecimalAsPercentage(getMarketYield(displayLoan.market_yield), 3)}
                  rawValue={displayLoan.market_yield}
                />
                <FormattedField
                  label="Probability of Default"
                  value={formatDecimalAsPercentage(getRiskMetric(displayLoan.pd), 2)}
                  rawValue={displayLoan.pd}
                />
                <FormattedField
                  label="Exposure at Default"
                  value={formatDecimalAsPercentage(getRiskMetric(displayLoan.ead), 2)}
                  rawValue={displayLoan.ead}
                />
                <FormattedField
                  label="Loss Given Default"
                  value={formatDecimalAsPercentage(getRiskMetric(displayLoan.lgd), 2)}
                  rawValue={displayLoan.lgd}
                />
              </div>
            </div>

            {/* Property Metrics */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Property Metrics
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">LTV Current:</span>
                  <span className="font-medium">
                    {displayLoan.ltv_current
                      ? formatDecimalAsPercentage(displayLoan.ltv_current, 2)
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">DSCR Current:</span>
                  <span className="font-medium">
                    {displayLoan.dscr_current
                      ? formatNumber(displayLoan.dscr_current, 2)
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Debt Yield:</span>
                  <span className="font-medium">
                    {displayLoan.debt_yield_current
                      ? formatDecimalAsPercentage(displayLoan.debt_yield_current, 2)
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">NOI:</span>
                  <span className="font-medium">
                    {displayLoan.noi ? formatCurrency(displayLoan.noi, 2, displayLoan.currency || 'USD') : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Interest Terms */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Interest Rate Terms
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Interest Type:</span>
                  <span className="font-medium">{displayLoan.interest_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Fixed Rate Coupon:</span>
                  <span className="font-medium">
                    {displayLoan.fixed_rate_coupon
                      ? formatDecimalAsPercentage(displayLoan.fixed_rate_coupon, 2)
                      : '—'}
                  </span>
                </div>
                {displayLoan.interest_type === 'Floating' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Floating Rate Index:</span>
                      <span className="font-medium">
                        {displayLoan.floating_rate_index || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Floating Rate Margin:</span>
                      <span className="font-medium">
                        {displayLoan.floating_rate_margin
                          ? formatDecimalAsPercentage(displayLoan.floating_rate_margin, 2)
                          : '—'}
                      </span>
                    </div>
                    {displayLoan.floating_rate_index && displayLoan.floating_rate_margin && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Index + Margin:</span>
                        <span className="font-medium">
                          {displayLoan.floating_rate_index} +{' '}
                          {formatDecimalAsPercentage(displayLoan.floating_rate_margin, 2)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Loan Structure */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Loan Structure
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Position:</span>
                  <span className="font-medium">
                    {displayLoan.position_in_capital_stack}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amortization Type:</span>
                  <span className="font-medium">{displayLoan.amortization_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Periodicity:</span>
                  <span className="font-medium">{displayLoan.periodicity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Origination Date:</span>
                  <span className="font-medium">
                    {formatDate(displayLoan.origination_date)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Maturity Date:</span>
                  <span className="font-medium">
                    {formatDate(displayLoan.original_maturity_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={() => {
                onEdit(displayLoan.rp_system_id);
                onClose();
              }}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit Loan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
